import { EventBus, GameEvents } from '@/events/EventBus';

/**
 * Tracks special resources (Co, Electro) earned during gameplay.
 * Persisted in localStorage across sessions.
 * Resources are separate from currency ($) and used for advanced workshop crafting.
 */

const STORAGE_KEY = 'robogame_resources';
const CHALLENGE_TREASURY_KEY = 'robogame_challenge_treasury';

export interface ResourceState {
  co: number;      // Connardium ore
  electro: number; // Electro scrap from Garbage
}

/** Accumulated challenge rewards (separate from campaign economy) */
export interface ChallengeTreasury {
  currency: number;  // $ accumulated from challenge victories
  co: number;
  electro: number;
  baseUpgradeLevels?: Record<string, number>; // BaseUpgradeAxis → level
}

class ResourceManagerClass {
  private _co: number = 0;
  private _electro: number = 0;
  /** When true, changes are NOT persisted to localStorage (challenge mode) */
  private _temporary: boolean = false;
  /** Starting Co/Electro when entering temporary mode (to compute earned delta) */
  private _tempStartCo: number = 0;
  private _tempStartElectro: number = 0;

  get co(): number { return this._co; }
  get electro(): number { return this._electro; }

  /** Add Co resource and emit event */
  addCo(amount: number): void {
    this._co += amount;
    this.save();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  /** Add Electro resource and emit event */
  addElectro(amount: number): void {
    this._electro += amount;
    this.save();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  /** Spend Co (returns false if not enough) */
  spendCo(amount: number): boolean {
    if (this._co < amount) return false;
    this._co -= amount;
    this.save();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
    return true;
  }

  /** Spend Electro (returns false if not enough) */
  spendElectro(amount: number): boolean {
    if (this._electro < amount) return false;
    this._electro -= amount;
    this.save();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
    return true;
  }

  getState(): ResourceState {
    return { co: this._co, electro: this._electro };
  }

  loadFromState(state: ResourceState): void {
    this._co = state.co ?? 0;
    this._electro = state.electro ?? 0;
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  /** Load from localStorage */
  load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as ResourceState;
        this._co = data.co ?? 0;
        this._electro = data.electro ?? 0;
      }
    } catch (e) { console.warn('[ResourceManager] load failed:', e); }
  }

  /** Save to localStorage (skipped in temporary/challenge mode) */
  save(): void {
    if (this._temporary) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.getState()));
    } catch (e) { console.warn('[ResourceManager] save failed:', e); }
  }

  /** Enter temporary mode: start with accumulated challenge treasury Co/Electro
   *  without touching campaign localStorage.
   *  Used for challenge/quick-play maps where resources are separate from campaign. */
  enterTemporary(): void {
    this._temporary = true;
    const treasury = this.getChallengeTreasury();
    this._co = treasury.co;
    this._electro = treasury.electro;
    this._tempStartCo = this._co;
    this._tempStartElectro = this._electro;
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  /** Returns Co/Electro earned during the current temporary session (delta from start). */
  getEarned(): ResourceState {
    if (!this._temporary) return { co: 0, electro: 0 };
    return {
      co: Math.max(0, this._co - this._tempStartCo),
      electro: Math.max(0, this._electro - this._tempStartElectro),
    };
  }

  /** Exit temporary mode and restore persisted campaign resources from localStorage. */
  exitTemporary(): void {
    this._temporary = false;
    this.load();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  /** Reset all resources */
  reset(): void {
    this._co = 0;
    this._electro = 0;
    this.save();
    EventBus.emit(GameEvents.RESOURCES_UPDATED, this.getState());
  }

  // ── Challenge Treasury (accumulates between challenge maps) ──

  /** Read the accumulated challenge treasury from localStorage */
  getChallengeTreasury(): ChallengeTreasury {
    try {
      const raw = localStorage.getItem(CHALLENGE_TREASURY_KEY);
      if (raw) return JSON.parse(raw) as ChallengeTreasury;
    } catch (e) { console.warn('[ResourceManager] challenge treasury load failed:', e); }
    return { currency: 0, co: 0, electro: 0 };
  }

  /** Add rewards to the challenge treasury (call on challenge victory) */
  addToChallengeTreasury(currency: number, co: number, electro: number): void {
    const t = this.getChallengeTreasury();
    t.currency += currency;
    t.co += co;
    t.electro += electro;
    try {
      localStorage.setItem(CHALLENGE_TREASURY_KEY, JSON.stringify(t));
    } catch (e) { console.warn('[ResourceManager] challenge treasury save failed:', e); }
  }

  /** Update base upgrade levels in the challenge treasury */
  setChallengeBaseUpgradeLevels(levels: Record<string, number>): void {
    const t = this.getChallengeTreasury();
    t.baseUpgradeLevels = levels;
    try {
      localStorage.setItem(CHALLENGE_TREASURY_KEY, JSON.stringify(t));
    } catch (e) { console.warn('[ResourceManager] challenge treasury save failed:', e); }
  }

  /** Reset the challenge treasury (e.g. new campaign slot) */
  resetChallengeTreasury(): void {
    localStorage.removeItem(CHALLENGE_TREASURY_KEY);
  }
}

export const ResourceManager = new ResourceManagerClass();
